import { setMockToken, setMockTokenApp } from './helpers';
import { signInWithMockEmail, signInWithMockPhone } from './signin';
import { UniversalLoginApi } from '../types';
import { toSignInViewModel } from '@apis/responseTransformer';
import { forgotPasswordMockEmail, forgotPasswordMockPhone } from '@apis/universalLogin/__mocks__/forgotPassword';
import {
    ChangePasswordViewModel,
    ForgotPasswordEmailViewModel,
    ForgotPasswordPhoneViewModel,
    OAuthViewModel,
    OtpLoginViewModel,
    SignInViewModel,
    SignupViewModel,
    DeleteAccountViewModel,
    OtpChannelsViewModel,
    OtpGenerateViewModel,
    OtpVerifyViewModel,
    BaseViewModel,
    UserPasskeyViewModel,
    UserPasskeysViewModel,
} from '@apis/model';
import {
    ForgotPasswordEmailRequest,
    LoginRequest,
    LoginFlow,
    ResponseCode,
    OAuthRequest,
    SignupRequest,
    OtpType,
    ChangePasswordRequest,
    ForgotPasswordPhoneRequest,
    OtpLoginRequest,
    GdprEraseStatus,
    SendOtpRequest,
    VerifyOtpRequest,
    OtpChannelStatus,
    PhoneAssociationRequest,
    WhatsappLinkRequest,
    UnifiedAuthRequest,
    WalletResetPinRequest,
    WalletScaRequest,
    WalletAuthRequest,
    ScaInitRequest,
    ConsentInfoRequest,
    ConsentInfoResponse,
    UserPasskeyDeleteRequest,
    UserPasskeyUpdateFriendlyNameRequest,
} from '@ul/api';

import { otpLoginMock } from '@apis/universalLogin/__mocks__/otpLogin';
import { PhoneAssociationViewModel } from '@apis/model/phoneAssociation/PhoneAssociationViewModel';
import { SocialAssociationViewModel } from '@apis/model/socialAssociation/SocialAssociationViewModel';
import { SignInCallbackViewModel } from '@apis/model/signIn/SignInCallbackViewModel';
import { UnifiedAuthViewModel } from '@apis/model/unifiedAuth/UnifiedAuthViewModel';
import { checkCaptcha, checkSignup, hasCaptcha, hasOtp, hasSocialConsent } from './unifiedAuth';
import { MFAOperationCode } from '@ul/common';
import { WalletViewModel } from '@apis/model/wallet/WalletViewModel';
import { ScaInitViewModel } from '@apis/model/wallet/ScaInitViewModel';
import {
    PasskeyRegistrationOptionsResponse,
    PasskeyAuthenticationOptionsResponse,
    RegisterPasskeyResponseViewModel,
} from '@apis/model';
import { PasskeyAuthenticateResponse } from '@ul/api';
import { RegisterPasskeyRequest } from '../RegisterPasskeyRequest';
import { AuthenticateWithPasskeyRequest } from '../AuthenticateWithPasskeyRequest';
import { passkeys } from './passkey/passkeyList';

export class MockUniversalLoginApi implements UniversalLoginApi {
    // Toggle that flips between true/false on each registration attempt
    private static passkeyNeedOtpToggle = false;

    /**
     * Enable/disable alternating NeedOtp response for passkey registration (for testing)
     * When enabled, every other request will return NeedOtp
     */
    static enablePasskeyNeedOtpToggle(): void {
        this.passkeyNeedOtpToggle = true;
        console.log('[MockPasskeyAPI] NeedOtp alternating mode ENABLED');
    }

    /**
     * Disable alternating NeedOtp response
     */
    static disablePasskeyNeedOtpToggle(): void {
        this.passkeyNeedOtpToggle = false;
        console.log('[MockPasskeyAPI] NeedOtp alternating mode DISABLED');
    }
    public signIn(signInRequest: LoginRequest): Promise<SignInViewModel> {
        const authType = signInRequest.credentials.authType;
        if (authType === 'email') {
            return signInWithMockEmail(signInRequest).then(model => {
                if (model.success) {
                    if (signInRequest.credentials.username.indexOf('xenia') > -1) {
                        setMockTokenApp('signInRequest.credentials.username');
                    } else {
                        setMockToken(signInRequest.credentials.username);
                    }
                }
                return model;
            });
        }
        return signInWithMockPhone(signInRequest).then(model => {
            if (model.success) setMockToken(signInRequest.credentials.username);
            return model;
        });
    }

    public changePassword(request: ChangePasswordRequest, token: string): Promise<ChangePasswordViewModel> {
        setMockToken('changepassworduniversallogin@agoda.com');
        return Promise.resolve({
            responseCode: ResponseCode.Successful,
            success: true,
        });
    }

    public initialChangePassword(authType: string, token: string): Promise<ChangePasswordViewModel> {
        if (!authType || !token || token === 'invalid-token') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.UsernameNotFound,
            });
        }

        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            memberInfo:
                authType === 'phone'
                    ? {
                          phone: '+6661234567',
                          countryCode: '66',
                      }
                    : {
                          email: 'test@tester.com',
                      },
            captchaViewModel: {
                type: 'recaptcha',
                apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
            },
        });
    }

    public forgotPasswordEmail(request: ForgotPasswordEmailRequest): Promise<ForgotPasswordEmailViewModel> {
        return forgotPasswordMockEmail(request.email);
    }

    public forgotPasswordPhone(request: ForgotPasswordPhoneRequest): Promise<ForgotPasswordPhoneViewModel> {
        return forgotPasswordMockPhone(request);
    }

    public initialForgotPassword(): Promise<ForgotPasswordEmailViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            captchaViewModel: {
                type: 'recaptcha',
                apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
            },
        });
    }

    public initialOtpLogin(): Promise<OtpLoginViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            captchaViewModel: {
                type: 'recaptcha',
                apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
            },
        });
    }

    public otpLogin(request: OtpLoginRequest): Promise<OtpLoginViewModel> {
        return otpLoginMock(request);
    }

    public initialSignup(): Promise<SignupViewModel> {
        return Promise.resolve({
            success: false,
            responseCode: ResponseCode.Successful,
            captchaViewModel: {
                type: 'recaptcha',
                apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
            },
        });
    }

    public signup(signupRequest: SignupRequest): Promise<SignupViewModel> {
        if (signupRequest.credentials.authType === 'email') {
            switch (signupRequest.credentials.username) {
                case 'error@agoda.com':
                    return Promise.resolve({
                        success: false,
                        responseCode: ResponseCode.ErrorOccurred,
                    });
                case 'existing@agoda.com':
                    return Promise.resolve({
                        success: false,
                        responseCode: ResponseCode.UsernameExists,
                    });
            }
        } else {
            switch (signupRequest.credentials.username) {
                case '+6620012345':
                    return Promise.resolve(
                        toSignInViewModel({
                            success: false,
                            responseCode: ResponseCode.ErrorOccurred,
                        })
                    );
                case '+6630012345':
                    return Promise.resolve(
                        toSignInViewModel({
                            success: false,
                            responseCode: ResponseCode.UsernameExists,
                        })
                    );
                default:
                    if (!!signupRequest.otpVerifyInfo) {
                        if (signupRequest.otpVerifyInfo.code === '000000') {
                            return Promise.resolve({
                                success: false,
                                responseCode: ResponseCode.OtpVerificationFailed,
                            });
                        }
                    } else {
                        return Promise.resolve({
                            success: false,
                            responseCode: ResponseCode.NeedOtp,
                            otpViewModel: {
                                target: signupRequest.credentials.username,
                                token: '12345',
                                otpType: OtpType.Phone,
                            },
                        });
                    }
                    break;
            }
        }

        setMockToken(signupRequest.credentials.username);
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
        });
    }

    public deleteAccount(): Promise<DeleteAccountViewModel> {
        return Promise.resolve({
            success: true,
            status: GdprEraseStatus.DeletionIncomplete,
        });
    }

    public forceLogout(): Promise<BaseViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
        });
    }

    public oauth(signInRequest: OAuthRequest): Promise<OAuthViewModel> {
        const code = signInRequest.credentials.code;
        switch (code) {
            case 'invalid_token':
                return Promise.resolve({
                    success: false,
                    responseCode: ResponseCode.ErrorOccurred,
                });
            // added for mock test on app
            case 'UNIVERSALLOGIN:GOOGLE:SUCCESS'.toLowerCase():
            case 'UNIVERSALLOGIN:GOOGLE:SUCCESS':
            case 'valid_token':
                // setMockTokenApp(code);
                setMockToken('socialuniversallogin@agoda.com');
                return Promise.resolve({
                    success: true,
                    responseCode: ResponseCode.NeedConsentInfo,
                    captchaViewModel: undefined,
                    userInfo: {
                        firstName: 'Mocked',
                        lastName: 'User',
                    },
                });
            case 'UNIVERSALLOGIN:WECHAT:SUCCESS'.toLowerCase():
            case 'UNIVERSALLOGIN:FACEBOOK:SUCCESS'.toLowerCase():
            case 'UNIVERSALLOGIN:WECHAT:SUCCESS':
            case 'UNIVERSALLOGIN:FACEBOOK:SUCCESS':
                setMockTokenApp(code);
                return Promise.resolve({
                    success: true,
                    responseCode: ResponseCode.Successful,
                });
            default:
                setMockToken('socialuniversallogin@agoda.com');
                return Promise.resolve({
                    success: true,
                    responseCode: ResponseCode.Successful,
                });
        }
    }

    public getChannels(transactionId: string): Promise<OtpChannelsViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            status: OtpChannelStatus.Success,
            message: 'test message',
            channels: [
                {
                    target: '1111',
                    otpType: OtpType.Phone,
                    channelDetail: '(***)*****-1111',
                    channelLabel: 'test channelLabel',
                },
                {
                    target: '2222',
                    otpType: OtpType.Text,
                    channelDetail: '(***)*****-1111',
                    channelLabel: 'test channelLabel',
                },
                {
                    target: '8888',
                    otpType: OtpType.Text,
                    channelDetail: '(***)*****-2222',
                    channelLabel: 'test channelLabel',
                },
                {
                    target: 'test@email.com',
                    otpType: OtpType.Email,
                    channelDetail: '(***)*****-email',
                    channelLabel: 'test channelLabel',
                },
                {
                    target: 'test@email.com',
                    otpType: OtpType.EmailAndPush,
                    channelDetail: '(***)*****-push',
                    channelLabel: 'test channelLabel',
                },
            ],
        });
    }

    public generateOtp(sendOtpRequest: SendOtpRequest, transactionId: string): Promise<OtpGenerateViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            otpViewModel: {
                target: sendOtpRequest.username,
                token: 'test token',
                otpType: sendOtpRequest.otpType || OtpType.Text,
            },
        });
    }

    public verifyOtp(verifyOtpRequest: VerifyOtpRequest, transactionId: string): Promise<OtpVerifyViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            otpType: verifyOtpRequest.otpVerifyInfo.otpType || OtpType.Text,
            solved: true,
            token: 'test token',
            memberId: 1234,
            otpClaims: 'test claim',
        });
    }

    public verifyOtpV2(verifyOtpRequest: VerifyOtpRequest): Promise<OtpVerifyViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            otpType: verifyOtpRequest.otpVerifyInfo.otpType || OtpType.Text,
            solved: true,
            token: 'test token',
            memberId: 1234,
            otpClaims: 'test claim',
        });
    }

    public verifyAuthedOtp(verifyOtpRequest: VerifyOtpRequest): Promise<OtpVerifyViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            otpType: verifyOtpRequest.otpVerifyInfo.otpType || OtpType.Text,
            solved: true,
            token: 'test token',
            memberId: 1234,
            otpClaims: 'test claim',
        });
    }

    public sendAuthedOtp(): Promise<OtpGenerateViewModel> {
        return Promise.resolve({
            success: false,
            responseCode: ResponseCode.Successful,
            otpViewModel: {
                target: '+66987244260',
                token: 'test token',
                otpType: OtpType.Phone,
            },
        });
    }

    public initializePhoneAssociation(): Promise<PhoneAssociationViewModel> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.NeedCaptcha,
            captchaViewModel: {
                type: 'recaptcha',
                apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
            },
        });
    }

    public verifyPhoneAssociation(
        verifyPhoneAssociationRequest: PhoneAssociationRequest
    ): Promise<PhoneAssociationViewModel> {
        if (!verifyPhoneAssociationRequest.otpVerifyInfo) {
            if (verifyPhoneAssociationRequest.phoneAssociationInfo?.nationalNumber === '12345678') {
                return Promise.resolve({
                    success: false,
                    responseCode: ResponseCode.InvalidPhoneNumber,
                });
            }
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.NeedOtp,
                captchaViewModel: {
                    type: 'recaptcha',
                    apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
                },
                otpViewModel: {
                    target: '+66987244260',
                    token: 'test token',
                    otpType: OtpType.Phone,
                },
            });
        }
        if (verifyPhoneAssociationRequest.otpVerifyInfo.code === '123456') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.OtpVerificationFailed,
            });
        }
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
        });
    }

    public verifyWhatsappLink(verifyWhatsappLinkRequest: WhatsappLinkRequest): Promise<PhoneAssociationViewModel> {
        if (!verifyWhatsappLinkRequest.otpVerifyInfo) {
            if (verifyWhatsappLinkRequest.phoneAssociationInfo?.nationalNumber === '12345678') {
                return Promise.resolve({
                    success: false,
                    responseCode: ResponseCode.InvalidPhoneNumber,
                });
            }
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.NeedOtp,
                captchaViewModel: {
                    type: 'arkose', // pragma: allowlist secret
                    apiKey: 'C88EDB2B-0221-41F2-A76C-73ACCA80516F', // pragma: allowlist secret
                    _type: 'com.agoda.universallogin.models.captcha.ArkoseInfo',
                },
                otpViewModel: {
                    target: '+66987244260',
                    token: 'test token',
                    otpType: OtpType.Whatsapp,
                },
            });
        }
        if (verifyWhatsappLinkRequest.otpVerifyInfo.code === '123456') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.OtpVerificationFailed,
            });
        }
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
        });
    }

    public associateSocial(socialType: string, request: any): Promise<SocialAssociationViewModel> {
        return Promise.resolve({
            success: true,
            statusCode: 200,
            message: 'Social account linked successfully',
        });
    }

    public signInCallback(loginFlow: LoginFlow = LoginFlow.UnifiedAuth): Promise<SignInCallbackViewModel> {
        // Check URL param to control response (for E2E testing)
        // Add ?signInCallbackSuccess=true to return Successful instead of NeedPasskeyRegistration
        // In iframe mode, read from parent.location; otherwise from window.location
        let forceSuccess = false;
        try {
            const isIframe = window.self !== window.top;
            const searchString = isIframe ? window.parent.location.search : window.location.search;
            const urlParams = new URLSearchParams(searchString);
            forceSuccess = urlParams.get('signInCallbackSuccess') === 'true';
        } catch (e) {
            // Cross-origin iframe - fall back to window.location
            const urlParams = new URLSearchParams(window.location.search);
            forceSuccess = urlParams.get('signInCallbackSuccess') === 'true';
        }

        return Promise.resolve({
            success: true,
            responseCode: forceSuccess ? ResponseCode.Successful : ResponseCode.NeedPasskeyRegistration,
        });
    }

    public validateToken(): Promise<BaseViewModel> {
        setMockToken('signInRequest.credentials.username');
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
        });
    }

    public resetPin(walletRequest: WalletResetPinRequest): Promise<WalletViewModel> {
        if (walletRequest.newPin === '111112') {
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.NeedPasskeyRegistration,
            });
        }
        if (walletRequest.newPin) {
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.Successful,
            });
        }
        if (walletRequest.dob === '26/12/2006') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.DobRequired,
            });
        }
        if (walletRequest.dob === '13/12/2013') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.RequestBlocked,
            });
        }
        if (walletRequest.dob) {
            if (walletRequest.otpVerifyInfo?.code === '123456') {
                return Promise.resolve({
                    success: false,
                    responseCode: ResponseCode.NeedPinInput,
                });
            }
            if (walletRequest.otpVerifyInfo?.code === '131313') {
                return Promise.resolve({
                    success: false,
                    responseCode: ResponseCode.RequestBlocked,
                    windowsDurationSec: 86400,
                });
            }
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.NeedOtp,
                otpViewModel: {
                    target: 'test@test.com',
                    token: '12345',
                    otpType: OtpType.Email,
                },
            });
        }
    }

    public walletAuth(walletRequest: WalletAuthRequest): Promise<WalletViewModel> {
        console.log('walletAuth mock', { walletRequest });
        // if (walletRequest.mfaOperation === MFAOperationCode.WalletStatement) {
        //     return Promise.resolve({
        //         success: false,
        //         responseCode: ResponseCode.NeedPinAndOtp,
        //     });
        // }
        if (walletRequest.otpVerifyInfo?.code === '555555') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.OtpVerificationFailed,
            });
        }
        if (walletRequest.otpVerifyInfo?.code === '000000') {
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.NeedPasskeyRegistration,
            });
        }
        if (walletRequest.otpVerifyInfo?.code === '123456') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.NeedPinInput,
            });
        }
        if (walletRequest?.pin === '131313') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.IncorrectPin,
            });
        }
        if (walletRequest?.pin === '666666') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.ErrorOccurred,
            });
        }
        if (walletRequest?.pin === '313131') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.RequestBlocked,
                windowsDurationSec: 3600,
            });
        }
        if (walletRequest.checkPasskey) {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.NeedPinInput,
                passkey: { has: true },
            });
        }

        if (walletRequest?.pin === '777777') {
            console.log('need otp mock');
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.NeedOtp,
            });
        }

        return Promise.resolve({
            success: false,
            responseCode: ResponseCode.NeedPinInput,
        });
    }

    public scaAuthen(walletRequest: WalletScaRequest, transactionId?: string): Promise<BaseViewModel> {
        console.log({ walletRequest, transactionId });
        if (walletRequest?.pin === '666666') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.NeedOtp,
                otpViewModel: {
                    target: 'test@test.com',
                    token: '12345',
                    otpType: OtpType.Email,
                    refCode: 'ABC123',
                },
            });
        }
        if (walletRequest?.pin === '131313') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.IncorrectPin,
            });
        }
        if (walletRequest?.pin === '777777') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.NeedOtp,
            });
        }
        if (walletRequest?.otpVerifyInfo?.code === '000000') {
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.NeedPasskeyRegistration,
            });
        }
        if (walletRequest?.otpVerifyInfo?.code === '123456') {
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.Successful,
            });
        }
        if (walletRequest?.pin === '313131' || walletRequest?.otpVerifyInfo?.code === '313131') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.RequestBlocked,
            });
        }
        return Promise.resolve({
            success: false,
            responseCode: ResponseCode.NeedPinInput,
        });
    }

    public scaInit(request?: ScaInitRequest): Promise<ScaInitViewModel> {
        console.log('[MockUniversalLoginApi] scaInit', { scaInitRequest: request });

        const mockResponse = {
            success: false,
            //TODO: in future we can add more like preferredPasskey:true/false, etc.
            passkey: { has: true },
            responseCode: ResponseCode.NeedPinAndOtp,
            downStreamServiceFailure: false,
        };
        console.log('[MockUniversalLoginApi] scaInit response', { mockResponse });
        return Promise.resolve(mockResponse);
    }

    public signUpConsent(ConsentInfoRequest: ConsentInfoRequest): Promise<ConsentInfoResponse> {
        return Promise.resolve({
            success: true,
            responseCode: ResponseCode.Successful,
            downStreamServiceFailure: false,
        });
    }

    public unifiedAuth(authLoginRequest: UnifiedAuthRequest): Promise<UnifiedAuthViewModel> {
        const errorCodes: Record<string, ResponseCode> = {
            'timeout@mock.com': ResponseCode.RequestTimeout,
            'network@mock.com': ResponseCode.NetworkError,
            'badrequest@mock.com': ResponseCode.ClientBadRequest,
            'notfound@mock.com': ResponseCode.ClientNotFound,
            'error@mock.com': ResponseCode.ErrorOccurred,
        };
        const errorCode = errorCodes[authLoginRequest?.email];
        if (errorCode !== undefined) {
            return Promise.resolve({
                success: false,
                responseCode: errorCode,
            });
        }

        if (authLoginRequest?.email.includes('arkose')) {
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.NeedSignupInfo,
                captchaViewModel: {
                    type: 'arkose',
                    apiKey: 'C88EDB2B-0221-41F2-A76C-73ACCA80516F',
                },
            });
        }

        // Check if the request if Whatsapp OTP login
        if (authLoginRequest?.whatsapp) {
            // Check if the request has captcha result
            if (hasCaptcha(authLoginRequest)) {
                // If the whatsapp number is '111111111', return NeedSignupInfo (for testing purpose - assuming it is a new user and don't have memberId)
                if (authLoginRequest?.whatsapp.includes('111111111')) {
                    return Promise.resolve({
                        success: true,
                        responseCode: ResponseCode.NeedSignupInfo,
                        captchaViewModel: {
                            type: 'arkose', // pragma: allowlist secret
                            apiKey: 'C88EDB2B-0221-41F2-A76C-73ACCA80516F', // pragma: allowlist secret
                        },
                    });
                } else {
                    // If the whatsapp number is not '111111111', return NeedOtp (for testing purpose - assuming it is an existing user and have memberId)
                    return Promise.resolve({
                        success: true,
                        responseCode: ResponseCode.NeedOtp,
                        otpViewModel: {
                            target: authLoginRequest.whatsapp,
                            token: '12345',
                            otpType: OtpType.Whatsapp,
                        },
                    });
                }
            } else {
                // If the request does not have captcha result, return NeedCaptcha
                return Promise.resolve({
                    success: false,
                    responseCode: ResponseCode.NeedCaptcha,
                    captchaViewModel: {
                        type: 'recaptcha', // pragma: allowlist secret
                        apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // pragma: allowlist secret
                    },
                });
            }
        }

        if (authLoginRequest?.email.includes('jtberror')) {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.UsernameNotFound,
            });
        }

        if (authLoginRequest?.otpVerifyInfo?.token === 'pls trigger captcha') {
            return Promise.resolve({
                success: false,
                responseCode: ResponseCode.NeedCaptcha,
                captchaViewModel: {
                    // type: 'arkose',
                    // apiKey: 'C88EDB2B-0221-41F2-A76C-73ACCA80516F',
                    type: 'recaptcha',
                    apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
                },
            });
        }

        if (hasOtp(authLoginRequest)) {
            setMockToken('signInRequest.credentials.username');
            return Promise.resolve({
                success: true,
                responseCode: ResponseCode.Successful,
            });
        }

        // Check signup
        if (checkSignup(authLoginRequest)) {
            if (hasSocialConsent(authLoginRequest)) {
                setMockToken(authLoginRequest.email);
                return Promise.resolve({
                    success: true,
                    responseCode: ResponseCode.Successful,
                });
            }

            if (!hasCaptcha(authLoginRequest) && !hasOtp(authLoginRequest)) {
                return Promise.resolve({
                    success: false,
                    responseCode: ResponseCode.NeedCaptcha,
                    captchaViewModel: {
                        type: 'recaptcha',
                        apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
                    },
                });
            }
        } else {
            // signin
            if (checkCaptcha(authLoginRequest)) {
                if (!hasCaptcha(authLoginRequest)) {
                    return Promise.resolve({
                        success: false,
                        responseCode: ResponseCode.NeedCaptcha,
                        captchaViewModel: {
                            type: 'recaptcha',
                            apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
                        },
                    });
                } else if (hasCaptcha(authLoginRequest)) {
                    return Promise.resolve({
                        success: false,
                        responseCode: ResponseCode.NeedSignupInfo,
                        captchaViewModel: {
                            // type: 'arkose',
                            // apiKey: 'C88EDB2B-0221-41F2-A76C-73ACCA80516F',
                            type: 'recaptcha',
                            apiKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
                        },
                    });
                }
            }
        }

        let mockResponseCode = ResponseCode.NeedOtp;
        if (authLoginRequest.checkPasskey) {
            mockResponseCode = ResponseCode.UserHasPasskey;
        }
        const result = {
            success: false,
            responseCode: mockResponseCode,
            otpViewModel: {
                target: authLoginRequest.email,
                token: '12345',
                otpType: OtpType.Email,
                hasPasskey: true,
            },
        };
        console.log(`unified/auth - response ${JSON.stringify(result)}`);
        return Promise.resolve(result);
    }

    public async getPasskeyRegistrationOptions(): Promise<PasskeyRegistrationOptionsResponse> {
        console.log('[MockPasskeyAPI] Calling backend /passkey/register/options');

        // Check current toggle state and flip it for next time
        const shouldReturnOtp = MockUniversalLoginApi.passkeyNeedOtpToggle;
        MockUniversalLoginApi.passkeyNeedOtpToggle = !MockUniversalLoginApi.passkeyNeedOtpToggle;

        console.log(
            `[MockPasskeyAPI] Will return OTP: ${shouldReturnOtp}, Next time: ${MockUniversalLoginApi.passkeyNeedOtpToggle}`
        );

        // Return NeedOtp response when toggle is true
        if (shouldReturnOtp) {
            console.log('[MockPasskeyAPI] 🔐 Returning NeedOtp response (testing mode)');
            return {
                success: false,
                responseCode: ResponseCode.NeedOtp,
                downStreamServiceFailure: false,
            };
        }

        try {
            const response = await fetch('/passkey/register/options', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: 'test-user-123',
                    userEmail: 'testuser@example.com',
                    userName: 'Test User',
                }),
            });

            const data = await response.json();
            console.log('[MockPasskeyAPI] Backend response:', data);

            // Transform backend response to match expected type
            return {
                success: data.success ?? true,
                responseCode: data.responseCode ?? ResponseCode.Successful,
                options: data.options,
                downStreamServiceFailure: data.downStreamServiceFailure ?? false,
            };
        } catch (error) {
            console.error('[MockPasskeyAPI] ❌ Backend call failed:', error);
            return {
                success: false,
                responseCode: ResponseCode.ErrorOccurred,
                downStreamServiceFailure: true,
            };
        }
    }

    public async registerPasskey(request: RegisterPasskeyRequest): Promise<RegisterPasskeyResponseViewModel> {
        const { responseJson } = request;

        console.log('[MockPasskeyAPI] Calling backend /passkey/register/verify', {
            responseJson,
        });

        try {
            const response = await fetch('/passkey/register/verify', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    responseJson,
                    userId: 'test-user-123',
                    userEmail: 'testuser@example.com',
                    userName: 'Test User',
                }),
            });

            const data = await response.json();
            console.log('[MockPasskeyAPI] Backend response:', data);

            return {
                success: data.success ?? true,
                responseCode: data.responseCode ?? ResponseCode.Successful,
                credential: data.credential,
                downStreamServiceFailure: data.downStreamServiceFailure ?? false,
            };
        } catch (error) {
            console.error('[MockPasskeyAPI] ❌ Backend call failed:', error);
            return {
                success: false,
                responseCode: ResponseCode.ErrorOccurred,
                downStreamServiceFailure: false,
            };
        }
    }

    /**
     * Initiate passkey authentication
     * Returns challenge options for WebAuthn authentication
     *
     * @returns Promise resolving to PasskeyAuthenticationOptionsResponse
     */
    public async getPasskeyAuthenticationOptions(): Promise<PasskeyAuthenticationOptionsResponse> {
        console.log('[MockPasskeyAPI] Calling backend /passkey/authenticate/options');

        try {
            const response = await fetch('/passkey/authenticate/options', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: 'test-user-123',
                }),
            });

            const data = await response.json();
            console.log('[MockPasskeyAPI] Backend response:', data);

            return {
                success: data.success ?? true,
                responseCode: data.responseCode ?? ResponseCode.Successful,
                options: data.options,
                downStreamServiceFailure: data.downStreamServiceFailure ?? false,
            };
        } catch (error) {
            console.error('[MockPasskeyAPI] ❌ Backend call failed:', error);
            return {
                success: false,
                responseCode: ResponseCode.ErrorOccurred,
                downStreamServiceFailure: false,
            };
        }
    }

    /**
     * Complete passkey authentication
     * Verifies the authentication response and returns sign-in result
     *
     * @param request - Authentication completion request with platform response
     * @returns Promise resolving to SignInViewModel
     */
    public async authenticateWithPasskey(
        request: AuthenticateWithPasskeyRequest
    ): Promise<PasskeyAuthenticateResponse> {
        const { responseJson } = request;

        console.log('[MockPasskeyAPI] Calling backend /passkey/authenticate/verify', {
            responseJson,
        });

        try {
            const response = await fetch('/passkey/authenticate/verify', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    responseJson,
                    userId: 'test-user-123',
                }),
            });

            const data = await response.json();
            console.log('[MockPasskeyAPI] Backend response:', data);

            // Set mock token if authentication was successful
            if (data.success && data.token) {
                setMockToken(data.userId || 'test-user-123');
            }

            return {
                success: data.success ?? true,
                responseCode: data.responseCode ?? ResponseCode.Successful,
                token: data.token,
                userId: data.userId,
                downStreamServiceFailure: data.downStreamServiceFailure ?? false,
            };
        } catch (error) {
            console.error('[MockPasskeyAPI] ❌ Backend call failed:', error);
            return {
                success: false,
                responseCode: ResponseCode.ErrorOccurred,
                downStreamServiceFailure: true,
            };
        }
    }

    public async getUserPasskeys(): Promise<UserPasskeysViewModel> {
        return {
            passkeys: passkeys,
            responseCode: ResponseCode.Successful,
            success: true,
        };
    }

    public async deleteUserPasskey(deletePasskeyRequest: UserPasskeyDeleteRequest): Promise<BaseViewModel> {
        return {
            responseCode: ResponseCode.Successful,
            success: true,
        };
    }

    public async updatePasskeyFriendlyName(
        updateRequest: UserPasskeyUpdateFriendlyNameRequest
    ): Promise<UserPasskeyViewModel> {
        return {
            responseCode: ResponseCode.Successful,
            success: true,
            passkey: passkeys[0],
        };
    }
}

// Expose toggle methods globally for easy testing in console
(window as any).enablePasskeyOtpTest = () => MockUniversalLoginApi.enablePasskeyNeedOtpToggle();
(window as any).disablePasskeyOtpTest = () => MockUniversalLoginApi.disablePasskeyNeedOtpToggle();
